import { ICellValue, ITableRow, ITableViewRowLock } from 'types'
import { FillDirection, IFillDragState, IViewPortColumn } from 'components/spreadsheet/types'
import { getRowValue, isColumnLockedByRules } from 'components/spreadsheet/helpers/functions'
import { BLOCK_FILL_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'

// --- Pattern types ---

interface CopyPattern {
  type: 'copy'
  value: ICellValue
}

interface LinearPattern {
  type: 'linear'
  start: number
  step: number
}

interface DatePattern {
  type: 'date'
  startDate: Date
  stepMs: number
  unit: 'day' | 'month' | 'year'
  stepCount: number
  endOfMonth?: boolean
  originalDay?: number
}

interface TextNumberPattern {
  type: 'text-number'
  prefix: string
  suffix: string
  start: number
  step: number
  padWidth: number
}

interface CyclePattern {
  type: 'cycle'
  values: ICellValue[]
}

type FillPattern = CopyPattern | LinearPattern | DatePattern | TextNumberPattern | CyclePattern

// --- Pattern detection ---

const parseNumber = (value: ICellValue): number | null => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    // Handle percentage strings like "50%"
    const pctMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)%$/)
    if (pctMatch) return parseFloat(pctMatch[1])
    const n = parseFloat(trimmed)
    return isNaN(n) ? null : n
  }
  return null
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

const parseDate = (value: ICellValue): Date | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  // Date-only values: parse as UTC to avoid timezone shifts
  if (DATE_ONLY_REGEX.test(trimmed)) {
    const [yearStr, monthStr, dayStr] = trimmed.split('-')
    const d = new Date(Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr)))
    return isNaN(d.getTime()) ? null : d
  }
  // Datetime values: interpret as UTC (append Z if no timezone present, matching codebase convention)
  const hasTimezone = /[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)
  const d = new Date(hasTimezone ? trimmed : `${trimmed}Z`)
  return isNaN(d.getTime()) ? null : d
}

const isLastDayOfMonth = (d: Date): boolean => {
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  return d.getUTCDate() === lastDay
}

const getLastDayOfMonth = (year: number, month: number): number => {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

const TEXT_NUMBER_REGEX = /^(.*?)(\d+)(\D*)$/

export const detectPattern = (values: ICellValue[], columnKind: string, direction?: FillDirection): FillPattern => {
  const reverse = direction === 'up' || direction === 'left'
  if (values.length === 0) return { type: 'copy', value: null }
  if (values.length === 1) {
    const single = values[0]

    // Numeric kinds: emit linear with step 1
    if (['integer', 'float', 'percentage', 'duration'].includes(columnKind)) {
      const n = parseNumber(single)
      if (n !== null) return { type: 'linear', start: n, step: 1 }
      return { type: 'copy', value: single }
    }

    // Text-with-numeric-suffix kinds
    if (['text', 'tag', 'variable', 'email', 'phone'].includes(columnKind) && typeof single === 'string') {
      const match = single.match(TEXT_NUMBER_REGEX)
      if (match) {
        const [, prefix, numStr, suffix] = match
        return {
          type: 'text-number',
          prefix,
          suffix,
          start: parseInt(numStr, 10),
          step: 1,
          padWidth: numStr.length
        }
      }
    }

    return { type: 'copy', value: single }
  }

  const allNull = values.every((v) => v === null || v === undefined || v === '')
  if (allNull) return { type: 'copy', value: null }

  // Numeric columns
  if (['integer', 'float', 'percentage', 'duration'].includes(columnKind)) {
    const nums = values.map(parseNumber)
    if (nums.every((n) => n !== null)) {
      const validNums = nums as number[]
      if (validNums.length >= 2) {
        const step = validNums[1] - validNums[0]
        const isLinear = validNums.every((n, i) => i === 0 || Math.abs(n - validNums[i - 1] - step) < 1e-9)
        if (isLinear) return { type: 'linear', start: reverse ? validNums[0] : validNums[validNums.length - 1], step }
      }
    }
  }

  // Date columns
  if (['date', 'datetime'].includes(columnKind)) {
    const dates = values.map(parseDate)
    if (dates.every((d) => d !== null)) {
      const validDates = dates as Date[]
      if (validDates.length >= 2) {
        // Check month differences FIRST (before days) to catch end-of-month patterns
        const monthDiffs = validDates.slice(1).map((d, i) => {
          const prev = validDates[i]
          return (d.getUTCFullYear() - prev.getUTCFullYear()) * 12 + (d.getUTCMonth() - prev.getUTCMonth())
        })
        const allMonthsSame = monthDiffs.every((m) => m === monthDiffs[0])
        const allEndOfMonth = validDates.every((d) => isLastDayOfMonth(d))
        if (allMonthsSame && monthDiffs[0] !== 0) {
          const sameDayOfMonth = validDates.every((d, i) => i === 0 || d.getUTCDate() === validDates[0].getUTCDate())

          if (sameDayOfMonth || allEndOfMonth) {
            const pattern: DatePattern = {
              type: 'date',
              startDate: reverse ? validDates[0] : validDates[validDates.length - 1],
              stepMs: 0,
              unit: 'month',
              stepCount: monthDiffs[0],
              endOfMonth: allEndOfMonth,
              originalDay: validDates[0].getUTCDate()
            }
            return pattern
          }
        }

        // Check day differences
        const dayDiffs = validDates.slice(1).map((d, i) => {
          return Math.round((d.getTime() - validDates[i].getTime()) / (24 * 60 * 60 * 1000))
        })
        const allDaysSame = dayDiffs.every((d) => d === dayDiffs[0])
        if (allDaysSame && dayDiffs[0] !== 0) {
          const pattern: DatePattern = {
            type: 'date',
            startDate: reverse ? validDates[0] : validDates[validDates.length - 1],
            stepMs: dayDiffs[0] * 24 * 60 * 60 * 1000,
            unit: 'day',
            stepCount: dayDiffs[0]
          }
          return pattern
        }

        // Check year differences
        const yearDiffs = validDates.slice(1).map((d, i) => d.getUTCFullYear() - validDates[i].getUTCFullYear())
        const allYearsSame = yearDiffs.every((y) => y === yearDiffs[0])
        if (
          allYearsSame &&
          yearDiffs[0] !== 0 &&
          validDates.every(
            (d, i) =>
              i === 0 ||
              (d.getUTCMonth() === validDates[0].getUTCMonth() && d.getUTCDate() === validDates[0].getUTCDate())
          )
        ) {
          return {
            type: 'date',
            startDate: reverse ? validDates[0] : validDates[validDates.length - 1],
            stepMs: 0,
            unit: 'year',
            stepCount: yearDiffs[0]
          }
        }
      }
    }
  }

  // Text with numeric suffix
  if (['text', 'tag', 'variable', 'email', 'phone'].includes(columnKind)) {
    const matches = values.map((v) => (typeof v === 'string' ? v.match(TEXT_NUMBER_REGEX) : null))
    if (matches.every((m) => m !== null)) {
      const validMatches = matches as RegExpMatchArray[]
      const prefix = validMatches[0][1]
      const suffix = validMatches[0][3]
      if (validMatches.every((m) => m[1] === prefix && m[3] === suffix)) {
        const numStrs = validMatches.map((m) => m[2])
        const nums = numStrs.map((s) => parseInt(s, 10))
        if (nums.length >= 2) {
          const step = nums[1] - nums[0]
          const isLinear = nums.every((n, i) => i === 0 || n - nums[i - 1] === step)
          if (isLinear) {
            const padWidth = Math.max(...numStrs.map((s) => s.length))
            return {
              type: 'text-number',
              prefix,
              suffix,
              start: reverse ? nums[0] : nums[nums.length - 1],
              step,
              padWidth
            }
          }
        }
      }
    }
  }

  // Fallback: cycle
  return { type: 'cycle', values }
}

// --- Value generation ---

const formatDateValue = (date: Date, columnKind: string): string => {
  if (columnKind === 'datetime') {
    // Strip milliseconds and trailing Z to match the canonical datetime format used elsewhere
    return date.toISOString().slice(0, 19)
  }
  // date only - YYYY-MM-DD (use UTC methods since parseDate creates UTC dates)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const generateValues = (
  pattern: FillPattern,
  count: number,
  direction: FillDirection,
  columnKind: string
): ICellValue[] => {
  if (count <= 0) return []

  const reverse = direction === 'up' || direction === 'left'
  const result: ICellValue[] = []

  for (let i = 0; i < count; i++) {
    const idx = reverse ? -(i + 1) : i + 1

    switch (pattern.type) {
      case 'copy':
        result.push(pattern.value)
        break

      case 'linear': {
        const val = pattern.start + pattern.step * idx
        if (columnKind === 'integer' || columnKind === 'duration') {
          result.push(Math.round(val))
        } else if (columnKind === 'percentage') {
          result.push(val)
        } else {
          result.push(parseFloat(val.toFixed(10)))
        }
        break
      }

      case 'date': {
        const base = new Date(pattern.startDate)
        if (pattern.unit === 'day') {
          base.setUTCDate(base.getUTCDate() + pattern.stepCount * idx)
        } else if (pattern.unit === 'month') {
          const targetMonth = base.getUTCMonth() + pattern.stepCount * idx
          base.setUTCDate(1) // avoid overflow when setting month
          base.setUTCMonth(targetMonth)
          if (pattern.endOfMonth) {
            base.setUTCDate(getLastDayOfMonth(base.getUTCFullYear(), base.getUTCMonth()))
          } else {
            const lastDay = getLastDayOfMonth(base.getUTCFullYear(), base.getUTCMonth())
            base.setUTCDate(Math.min(pattern.originalDay ?? base.getUTCDate(), lastDay))
          }
        } else if (pattern.unit === 'year') {
          base.setUTCFullYear(base.getUTCFullYear() + pattern.stepCount * idx)
        }
        result.push(formatDateValue(base, columnKind))
        break
      }

      case 'text-number': {
        const num = pattern.start + pattern.step * idx
        const numStr = String(Math.abs(num)).padStart(pattern.padWidth, '0')
        result.push(`${pattern.prefix}${num < 0 ? '-' : ''}${numStr}${pattern.suffix}`)
        break
      }

      case 'cycle': {
        const len = pattern.values.length
        if (reverse) {
          // Cycle backwards from the first source value so that after reverse() the sequence is seamless
          result.push(pattern.values[(len - 1 - (i % len)) % len])
        } else {
          result.push(pattern.values[i % len])
        }
        break
      }
    }
  }

  return result
}

// --- Fill cell computation ---

export interface IFillCell {
  rowId: string
  columnId: string
  value: ICellValue
  oldValue: ICellValue
}

export const computeFillCells = (
  fillDragState: IFillDragState,
  rows: ITableRow[],
  allColumns: IViewPortColumn[],
  columnIdToIndex: Map<string, number>,
  columnStore: CompressedColumnStore | null,
  rowLockSettings: ITableViewRowLock[]
): IFillCell[] => {
  const { sourceStartRowIndex, sourceEndRowIndex, sourceStartColId, sourceEndColId, targetRowIndex, direction } =
    fillDragState

  if (!direction) return []

  const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
  const colPositions = new Map(nonGroupedColumns.map((c, i) => [c.column.publicId, i]))

  // Determine source column range
  const srcStartColPos = colPositions.get(sourceStartColId)
  const srcEndColPos = colPositions.get(sourceEndColId)
  if (srcStartColPos == null || srcEndColPos == null) return []
  const minColPos = Math.min(srcStartColPos, srcEndColPos)
  const maxColPos = Math.max(srcStartColPos, srcEndColPos)

  // Get columns in source range
  const sourceColumns = nonGroupedColumns.filter((_, i) => i >= minColPos && i <= maxColPos)

  // Determine target row range (excluding source rows)
  const isVertical = direction === 'down' || direction === 'up'
  if (!isVertical) return [] // Horizontal fill not implemented in v1

  const minSourceRow = Math.min(sourceStartRowIndex, sourceEndRowIndex)
  const maxSourceRow = Math.max(sourceStartRowIndex, sourceEndRowIndex)

  let fillStartRow: number
  let fillEndRow: number

  if (direction === 'down') {
    fillStartRow = maxSourceRow + 1
    fillEndRow = targetRowIndex
  } else {
    fillStartRow = targetRowIndex
    fillEndRow = minSourceRow - 1
  }

  if (fillStartRow > fillEndRow) return []

  // Collect target data-row indices (skip group headers / add-row placeholders)
  const targetRowIndices: number[] = []
  for (let r = fillStartRow; r <= fillEndRow; r++) {
    if (r >= 0 && r < rows.length && rows[r]?.publicId) {
      targetRowIndices.push(r)
    }
  }
  if (targetRowIndices.length === 0) return []

  const result: IFillCell[] = []

  for (const col of sourceColumns) {
    const column = col.column
    // Skip read-only columns and blocked types (attachment, vote)
    if (
      isColumnLockedByRules(rowLockSettings, column.publicId) ||
      column.scriptEnabled ||
      column.formulaEnabled ||
      column.isJoined ||
      column.isSynced ||
      BLOCK_FILL_COLUMN_TYPES.includes(column.kind)
    )
      continue

    // Extract source values for this column
    const sourceValues: ICellValue[] = []
    for (let r = minSourceRow; r <= maxSourceRow; r++) {
      if (r >= 0 && r < rows.length && rows[r]?.publicId) {
        sourceValues.push(getRowValue(rows[r], column.publicId, columnIdToIndex, columnStore))
      }
    }

    const pattern = detectPattern(sourceValues, column.kind, direction)
    const fillValues = generateValues(pattern, targetRowIndices.length, direction, column.kind)

    // For upward fill, reverse so the furthest value from source goes to the topmost row
    if (direction === 'up') fillValues.reverse()

    for (let i = 0; i < targetRowIndices.length; i++) {
      const row = rows[targetRowIndices[i]]

      const oldValue = getRowValue(row, column.publicId, columnIdToIndex, columnStore)
      result.push({
        rowId: row.publicId,
        columnId: column.publicId,
        value: fillValues[i],
        oldValue
      })
    }
  }

  return result
}

// --- Double-click fill target detection ---

/**
 * Find the last row index to fill down to when the user double-clicks the fill handle.
 * Uses the adjacent column to the left (or right if leftmost) to determine extent:
 * walks downward from the source range and stops at the last consecutive row with data.
 */
export const findFillDownTargetRow = (
  sourceEndRowIndex: number,
  sourceColId: string,
  rows: ITableRow[],
  allColumns: IViewPortColumn[],
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
): number | null => {
  const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
  const colPositions = new Map(nonGroupedColumns.map((c, i) => [c.column.publicId, i]))
  const sourceColPos = colPositions.get(sourceColId)
  if (sourceColPos === undefined) return null

  // Choose the immediate adjacent column: prefer left, fallback to right if leftmost
  let adjacentPos: number | null = null
  if (sourceColPos > 0) {
    adjacentPos = sourceColPos - 1
  } else if (sourceColPos < nonGroupedColumns.length - 1) {
    adjacentPos = sourceColPos + 1
  }
  if (adjacentPos === null) return null

  const adjacentColId = nonGroupedColumns[adjacentPos].column.publicId

  // Walk down from the row after source end, stop when:
  // 1. The adjacent column has no data in this row, OR
  // 2. Source (fill) column already has data (Excel behaviour: stop before first filled cell)
  let lastRowWithData = sourceEndRowIndex
  for (let r = sourceEndRowIndex + 1; r < rows.length; r++) {
    const row = rows[r]
    if (!row?.publicId) continue // skip group headers / add-row placeholders
    const adjVal = getRowValue(row, adjacentColId, columnIdToIndex, columnStore)
    if (adjVal === null || adjVal === undefined || adjVal === '') break
    const sourceVal = getRowValue(row, sourceColId, columnIdToIndex, columnStore)
    if (sourceVal !== null && sourceVal !== undefined && sourceVal !== '' && sourceVal !== false) break
    lastRowWithData = r
  }

  // Only fill if there are rows to fill
  return lastRowWithData > sourceEndRowIndex ? lastRowWithData : null
}

// --- Fill range check for cell highlighting ---

export const isInFillRange = (
  rowIndex: number,
  columnId: string,
  fillDragState: IFillDragState,
  columnPositionMap: Map<string, number>
): boolean => {
  if (!fillDragState.direction) return false

  const { sourceStartRowIndex, sourceEndRowIndex, sourceStartColId, sourceEndColId, targetRowIndex, direction } =
    fillDragState

  const minSourceRow = Math.min(sourceStartRowIndex, sourceEndRowIndex)
  const maxSourceRow = Math.max(sourceStartRowIndex, sourceEndRowIndex)

  // Check row is in fill range (not source range)
  let inFillRowRange = false
  if (direction === 'down') {
    inFillRowRange = rowIndex > maxSourceRow && rowIndex <= targetRowIndex
  } else if (direction === 'up') {
    inFillRowRange = rowIndex < minSourceRow && rowIndex >= targetRowIndex
  }

  if (!inFillRowRange) return false

  // Check column is in source column range
  const srcStartPos = columnPositionMap.get(sourceStartColId)
  const srcEndPos = columnPositionMap.get(sourceEndColId)
  const currentPos = columnPositionMap.get(columnId)

  if (srcStartPos === undefined || srcEndPos === undefined || currentPos === undefined) return false

  const minCol = Math.min(srcStartPos, srcEndPos)
  const maxCol = Math.max(srcStartPos, srcEndPos)

  return currentPos >= minCol && currentPos <= maxCol
}
